import { Copy, Pencil, X } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Location } from "@/types/tms";
import { formatAddress } from "@/components/forms/AddressFields";
import { getLocationDisplayName } from "@/lib/location-utils";
import { toast } from "sonner";

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value || "—"}</span>
    </div>
  );
}

interface LocationDetailSheetProps {
  location: Location | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (location: Location) => void;
}

export function LocationDetailSheet({ location, open, onOpenChange, onEdit }: LocationDetailSheetProps) {
  if (!location) return null;

  const originText = `${location.origin_latitude.toFixed(6)}, ${location.origin_longitude.toFixed(6)}`;
  const destText = `${location.destination_latitude.toFixed(6)}, ${location.destination_longitude.toFixed(6)}`;

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="w-full lg:w-[440px] lg:max-w-[440px] max-w-full overflow-y-auto [&>button.absolute]:hidden p-0"
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { onOpenChange(false); onEdit(location); }}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="px-4 pb-4">
          <h2 className="text-lg font-semibold">{getLocationDisplayName(location)}</h2>
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            {location.track_in_yms && <Badge variant="secondary">YMS</Badge>}
          </div>
        </div>

        <div className="px-4 pb-4 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              <DetailRow label="Short Code" value={location.short_code} />
              <DetailRow label="Customer" value={(location as any).customer?.organization_name} />
              <DetailRow label="Address" value={formatAddress(location)} />
              <div className="flex justify-between items-start py-2">
                <span className="text-sm text-muted-foreground">Origin coordinates</span>
                <button onClick={() => copy(originText, "Origin coordinates")} className="flex items-center gap-1.5 text-sm font-mono font-medium hover:text-primary transition-colors">
                  {originText}
                  <Copy className="h-3 w-3" />
                </button>
              </div>
              <div className="flex justify-between items-start py-2">
                <span className="text-sm text-muted-foreground">Destination coordinates</span>
                <button onClick={() => copy(destText, "Destination coordinates")} className="flex items-center gap-1.5 text-sm font-mono font-medium hover:text-primary transition-colors">
                  {destText}
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}
